import type { LetterDoc } from "@gsignatures/core";
import { useRef, useState } from "react";
import { ActionBar } from "../components/ActionBar";
import { Icon } from "../components/Icon";
import { useFocusHeading } from "../lib/hooks";
import type { FlowCtx } from "../state/flow";
import { S } from "../strings";
import { DocViewer } from "../viewer/DocViewer";
import { letterOverlay } from "../viewer/LetterOverlay";

/** The letter's role, from its key terms, for the heading ("HR Assistant at Golden Sparrow"). */
export function roleOf(doc: LetterDoc): string | undefined {
  return doc.terms.find((t) => /^(role|position|title)$/i.test(t.label))?.value;
}

export function OfferScreen({ ctx, doc }: { ctx: FlowCtx; doc: LetterDoc }) {
  const { session, w, api } = ctx;
  const heading = useFocusHeading();
  const [busy, setBusy] = useState(false);
  const viewedSent = useRef(false);
  const accepted = !!session.progress.accepted[doc.id];
  const role = roleOf(doc);

  async function accept() {
    if (busy) return;
    if (accepted) {
      ctx.continueFrom(doc.id);
      return;
    }
    setBusy(true);
    const s = await ctx.run(() => api.accept(doc.id));
    setBusy(false);
    if (s) ctx.apply(s);
  }

  // The view is recorded with the fingerprint of the file shown, once the document is on screen.
  const onReady = () => {
    if (viewedSent.current) return;
    viewedSent.current = true;
    void api.viewed(doc.id, doc.sha256).catch(() => undefined);
  };

  return (
    <>
      <main className="page" id="main">
        <div className="split">
          <aside className="sticky-col">
            <div className="panel panel-pad stack">
              <div>
                <div className="eyebrow">{S.offer.eyebrow}</div>
                <h1 className="title" ref={heading} tabIndex={-1}>
                  {role ? S.offer.titleAt(role, w.sender) : doc.title}
                </h1>
                <p className="lede">{S.offer.lede}</p>
              </div>
              <dl className="terms" aria-label={S.offer.termsLabel}>
                {doc.terms.map((t) => (
                  <div className="term" key={t.label}>
                    <dt>{t.label}</dt>
                    <dd>{t.value}</dd>
                  </div>
                ))}
              </dl>
              {doc.companySigned && (
                <div className="note ok">
                  <Icon n="check" />
                  <span>{S.offer.companySigned(w)}</span>
                </div>
              )}
              {doc.companyCountersigns && (
                <div className="note">
                  <Icon n="pen" />
                  <span>{S.offer.companyCountersigns(w, doc.companyCountersigns.name)}</span>
                </div>
              )}
              {doc.mergedSpots.length > 0 && (
                <div className="note">
                  <Icon n="info" />
                  <span>{S.offer.mergedNote(w)}</span>
                </div>
              )}
              <div className="stack desk-actions tight">
                <button type="button" className="btn btn-primary btn-wide" onClick={() => void accept()} disabled={busy} aria-busy={busy}>
                  {S.offer.accept} <Icon n="chev" />
                </button>
                <button type="button" className="btn btn-danger btn-wide" onClick={() => ctx.openModal("decline")}>
                  {S.offer.decline}
                </button>
              </div>
            </div>
          </aside>
          <DocViewer
            title={S.offer.viewerName(doc.title, w.sender)}
            url={api.fileUrl(doc.id)}
            withCredentials={!api.fake}
            pageCount={doc.pages}
            textLayer
            downloadHref={api.fileUrl(doc.id)}
            {...(api.fake ? { demoPath: api.fileUrl(doc.id) } : {})}
            overlay={letterOverlay({ doc, w, mode: "read", signerName: session.signer.name })}
            onReady={onReady}
          />
        </div>
      </main>
      <ActionBar>
        <button type="button" className="btn btn-quiet" onClick={() => ctx.openModal("decline")}>
          {S.offer.declineShort}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void accept()} disabled={busy}>
          {S.offer.acceptShort}
        </button>
      </ActionBar>
    </>
  );
}
